/**
 * Paywall hook — wraps the RevenueCat helpers with React state and wires a
 * purchase/restore back into the app's membership tier.
 *
 * After a successful purchase the RevenueCat webhook mirrors the entitlement
 * into WooCommerce + Supabase; we poll `refreshMembership()` for a few seconds
 * so the rest of the app (server-gated content) unlocks without a reload. The
 * RevenueCat entitlement itself unlocks this screen's success state instantly.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CustomerInfo, PurchasesOffering, PurchasesPackage } from 'react-native-purchases';
import { useAuth } from '../AuthContext';
import { invalidateMembershipQueries } from '../membershipRefresh';
import { isMemberTier } from '../membershipTier';
import {
  getCurrentOffering,
  getOfferingById,
  getTrialEligibility,
  hasActiveEntitlement,
  purchasePackage,
  refreshCustomerInfo,
  restorePurchases,
} from './RevenueCat';

export type PurchaseOutcome =
  | { ok: true; customerInfo?: CustomerInfo | null }
  | { ok: false; cancelled?: true; error?: unknown };

// RevenueCat surfaces StoreKit failures as numeric-string codes. The three
// below are the ones a tester or a real user actually hits, and each needs its
// own instruction — "something went wrong" leaves them stuck.
const PURCHASE_ERROR_MESSAGES: Record<string, string> = {
  // RECEIPT_ALREADY_IN_USE_ERROR — this Apple ID's subscription belongs to
  // another Open Studio account.
  '7': "This Apple ID's subscription is already linked to another Open Studio account. Sign in to that account, or use Restore purchases to move it to this one.",
  // PAYMENT_PENDING_ERROR — Ask to Buy / pending parental approval.
  '20': 'Your purchase is waiting for approval. Access unlocks as soon as Apple confirms it.',
  // STORE_PROBLEM_ERROR — App Store outage or sandbox hiccup.
  '2': "The App Store couldn't complete the purchase right now. Please try again in a moment.",
};

function purchaseErrorMessage(err: unknown): string {
  const code = String((err as { code?: unknown })?.code ?? '');
  return (
    PURCHASE_ERROR_MESSAGES[code] ??
    'Something went wrong completing your purchase. Please try again.'
  );
}

async function pollMembership(refresh: () => Promise<void>): Promise<void> {
  const delaysMs = [0, 1500, 2500, 4000];
  for (const d of delaysMs) {
    if (d) await new Promise((r) => setTimeout(r, d));
    await refresh();
  }
}

export function usePurchases(offeringId?: string) {
  const { refreshMembership, membershipTier, user } = useAuth();
  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [trialEligibility, setTrialEligibility] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Named offering (e.g. 'pro' for the upgrade flow) or the current one.
    const o = offeringId ? await getOfferingById(offeringId) : await getCurrentOffering();
    setOffering(o);
    setLoading(false);
    const ids = (o?.availablePackages ?? []).map((p) => p.product.identifier);
    setTrialEligibility(await getTrialEligibility(ids));
  }, [offeringId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const buy = useCallback(
    async (pkg: PurchasesPackage): Promise<PurchaseOutcome> => {
      setPurchasing(true);
      setError(null);
      try {
        const info = await purchasePackage(pkg);
        if (!info) return { ok: false, cancelled: true }; // user cancelled
        await pollMembership(refreshMembership);
        await invalidateMembershipQueries(user?.id);
        const fresh = (await refreshCustomerInfo()) ?? info;
        return { ok: true, customerInfo: fresh };
      } catch (e) {
        setError(purchaseErrorMessage(e));
        return { ok: false, error: e };
      } finally {
        setPurchasing(false);
      }
    },
    [refreshMembership, user?.id],
  );

  const restore = useCallback(async (): Promise<PurchaseOutcome> => {
    setPurchasing(true);
    setError(null);
    try {
      const info = await restorePurchases();
      await pollMembership(refreshMembership);
      await invalidateMembershipQueries(user?.id);
      if (hasActiveEntitlement(info)) return { ok: true };
      setError('No previous purchases found for this Apple ID.');
      return { ok: false };
    } finally {
      setPurchasing(false);
    }
  }, [refreshMembership, user?.id]);

  return {
    offering,
    /** product identifier → true when this Apple ID can still use the intro offer. */
    trialEligibility,
    loading,
    purchasing,
    error,
    buy,
    restore,
    reload,
    /** True if the signed-in user already has a paid membership (any source). */
    isMember: isMemberTier(membershipTier),
  };
}
